import {createSlice, isAnyOf, PayloadAction} from "@reduxjs/toolkit";
import {carUpserted, eventReceived} from "../../board/model/slice";
import {carMutations, fetchUnshipCar} from "../../board/model/actions";
import {ICar} from "../../board/model/types";
import {
    fetchAddHoliday,
    fetchRemoveHoliday,
    fetchSaveShift,
    fetchSchedule,
    fetchStatsCar,
    fetchStatsCars,
    fetchSummary,
} from "./actions";
import {ISchedule, IStatsCar, IStatsCarDetail, IStatsSummary} from "./types";

export interface IStatsState {
    cars: IStatsCar[];
    carsLoading: boolean;
    summary: IStatsSummary | null;
    /**
     * Панель машины в Стате. Карточка живёт здесь, а не в `board.cars`: там уехавшей
     * не место (S17.2) — она появилась бы на доске в «На отгрузку».
     */
    detail: IStatsCarDetail | null;
    schedule: ISchedule | null;
    /** Последние запросы списка и сводки: ответ старого (сменили период, выбор) не перетирает свежий. */
    req: {cars: string | null; summary: string | null; detail: string | null};
    /** Растёт, когда цифры устарели (уехала/вернулась, смены, праздники): страница перечитывает их. */
    stale: number;
}

export const initialState: IStatsState = {
    cars: [],
    carsLoading: false,
    summary: null,
    detail: null,
    schedule: null,
    req: {cars: null, summary: null, detail: null},
    stale: 0,
};

export const StatsSlice = createSlice({
    name: "stats",
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder.addCase(fetchStatsCars.pending, (s, a) => {
            s.carsLoading = true;
            s.req.cars = a.meta.requestId;
        });
        builder.addCase(fetchStatsCars.fulfilled, (s, a) => {
            if (s.req.cars !== a.meta.requestId) return;
            s.cars = a.payload;
            s.carsLoading = false;
        });
        builder.addCase(fetchStatsCars.rejected, (s, a) => {
            if (s.req.cars === a.meta.requestId) s.carsLoading = false;
        });
        builder.addCase(fetchSummary.pending, (s, a) => {
            s.req.summary = a.meta.requestId;
        });
        builder.addCase(fetchSummary.fulfilled, (s, a) => {
            if (s.req.summary === a.meta.requestId) s.summary = a.payload;
        });
        // Клик по A, потом по B: запоздавший ответ A не подменяет панель B.
        builder.addCase(fetchStatsCar.pending, (s, a) => {
            s.req.detail = a.meta.requestId;
        });
        builder.addCase(fetchStatsCar.fulfilled, (s, a) => {
            if (s.req.detail === a.meta.requestId) s.detail = a.payload;
        });
        builder.addCase(fetchSchedule.fulfilled, (s, a) => {
            s.schedule = a.payload;
        });
        builder.addCase(fetchSaveShift.fulfilled, (s, a) => {
            if (s.schedule) s.schedule.stages = s.schedule.stages.map((x) => (x.stage === a.payload.stage ? a.payload : x));
            s.stale += 1;
        });
        builder.addCase(fetchAddHoliday.fulfilled, (s, a) => {
            if (s.schedule) s.schedule.holidays = [...s.schedule.holidays, a.payload].sort((x, y) => x.date.localeCompare(y.date));
            s.stale += 1;
        });
        builder.addCase(fetchRemoveHoliday.fulfilled, (s, a) => {
            if (s.schedule) s.schedule.holidays = s.schedule.holidays.filter((h) => h.id !== a.payload.id);
            s.stale += 1;
        });
        // Вернули на доску — машины в Стате больше нет; сводку перечитает событие `unshipped`.
        builder.addCase(fetchUnshipCar.fulfilled, (s, a) => {
            s.cars = s.cars.filter((c) => c.id !== a.payload.id);
        });
        builder.addCase(eventReceived, (s, a) => {
            if (a.payload.type === "shipped" || a.payload.type === "unshipped") s.stale += 1;
        });
        // Открытая панель видит правки (вехи, заметки, чат) так же, как доска.
        builder.addMatcher(
            isAnyOf(carUpserted, ...carMutations.map((m) => m.fulfilled)),
            (s, a: PayloadAction<ICar>) => {
                if (s.detail?.car.id === a.payload.id) s.detail.car = {...s.detail.car, ...a.payload};
            },
        );
    },
});

export default StatsSlice.reducer;
